const allowedTags = new Set([
  'A',
  'BLOCKQUOTE',
  'BR',
  'EM',
  'H2',
  'H3',
  'H4',
  'I',
  'IMG',
  'LI',
  'OL',
  'P',
  'S',
  'STRONG',
  'U',
  'UL',
]);

const blockedTags = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META']);

const isSafeURL = (value: string, imageOnly = false) => {
  try {
    const parsed = new URL(value, window.location.origin);
    if (imageOnly) {
      return ['http:', 'https:'].includes(parsed.protocol);
    }

    return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const sanitizeNode = (node: Node): Node | null => {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as HTMLElement;
  if (blockedTags.has(element.tagName)) {
    return null;
  }

  if (!allowedTags.has(element.tagName)) {
    const fragment = document.createDocumentFragment();
    element.childNodes.forEach((child) => {
      const sanitizedChild = sanitizeNode(child);
      if (sanitizedChild) {
        fragment.appendChild(sanitizedChild);
      }
    });

    return fragment;
  }

  const sanitizedElement = document.createElement(element.tagName.toLowerCase());
  if (element.tagName === 'A') {
    const href = element.getAttribute('href') || '';
    if (isSafeURL(href)) {
      sanitizedElement.setAttribute('href', href);
      sanitizedElement.setAttribute('target', '_blank');
      sanitizedElement.setAttribute('rel', 'noopener noreferrer');
    }
  }

  if (element.tagName === 'IMG') {
    const src = element.getAttribute('src') || '';
    if (!isSafeURL(src, true)) {
      return null;
    }
    sanitizedElement.setAttribute('src', src);
    sanitizedElement.setAttribute('alt', element.getAttribute('alt') || '');
  }

  element.childNodes.forEach((child) => {
    const sanitizedChild = sanitizeNode(child);
    if (sanitizedChild) {
      sanitizedElement.appendChild(sanitizedChild);
    }
  });

  return sanitizedElement;
};

export const sanitizeEventDescriptionHTML = (value: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return '';
  }

  const parsed = new DOMParser().parseFromString(value, 'text/html');
  const output = document.createElement('div');
  parsed.body.childNodes.forEach((node) => {
    const sanitized = sanitizeNode(node);
    if (sanitized) {
      output.appendChild(sanitized);
    }
  });

  return output.innerHTML.trim();
};

export const htmlToText = (value: string) => {
  if (typeof window === 'undefined') {
    return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  const parsed = new DOMParser().parseFromString(value, 'text/html');
  return (parsed.body.textContent || '').replace(/\s+/g, ' ').trim();
};
